import { api } from "common";
import { colors } from "components/Theme/WebTheme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useAnalytics } from "../common/useAnalytics";
import CircularLoading from "../components/CircularLoading";
import DashboardNewUI from "../components/Dashboard/DashboardNewUI";
import DashboardOldUI from "../components/Dashboard/DashboardOldUI";
import { firebase } from "common/src/config/configureFirebase";
import { FirebaseConfig } from "config/FirebaseConfig";

const Dashboard = () => {
    // Analytics hook
    const { logPageView, logDriverHome } = useAnalytics();
    const { config } = firebase;
    const [mylocation, setMylocation] = useState(null);
    const [locationStatus, setLocationStatus] = useState("getting"); // 'getting', 'success', 'fallback'
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector((state) => state.usersdata);
    const bookinglistdata = useSelector((state) => state.bookinglistdata);
    const settingsdata = useSelector((state) => state.settingsdata);
    const auth = useSelector((state) => state.auth);
    const cars = useSelector((state) => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const countrylistdata = useSelector((state) => state.countrylistData);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(t("select_country"));
    const { fetchDriversGeoQuery, clearFetchDrivers } = api;
    const [data, setData] = useState([]);
    const [powerTranzActive, setPowerTranzActive] = useState(false);
    const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);

    // OPTIMIZATION: Use GeoQuery instead of direct Firebase listener (prevents infinite loop)
    const getDriversGeoQuery = useCallback(async () => {
        if (isLoadingDrivers) return;
        setIsLoadingDrivers(true);

        try {
            // Use same Cloud Function as MapScreen
            const response = await fetch(
                `https://us-central1-${config.projectId}.cloudfunctions.net/geoquery-geoqueryDrivers`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        limit: 5000, // Higher limit for dashboard to show all drivers
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                if (data.drivers) {
                    dispatch(fetchDriversGeoQuery(data.drivers));
                }
            }
        } catch (error) {
            console.error("Error fetching drivers geo query:", error);
        } finally {
            setIsLoadingDrivers(false);
        }
    }, [isLoadingDrivers, dispatch, fetchDriversGeoQuery]);

    // Fetch drivers on mount using GeoQuery
    useEffect(() => {
        getDriversGeoQuery();

        // OPTIONAL: Auto-refresh every 30 seconds (disabled by default untuk prevent overload)
        // Uncomment untuk enable auto-refresh:
        // const refreshInterval = setInterval(() => {
        //     getDriversGeoQuery();
        // }, 30000); // 30 seconds

        return () => {
            dispatch(clearFetchDrivers());
            // if (refreshInterval) clearInterval(refreshInterval);
        };
    }, [dispatch, clearFetchDrivers, getDriversGeoQuery]);

    useEffect(() => {
        if (mylocation == null) {
            setLocationStatus("getting");
            // Try to get user's current location
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log("Dashboard: Geolocation success");
                    setMylocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                    setLocationStatus("success");
                },
                (error) => {
                    console.error("Dashboard: Geolocation error:", error);
                    console.error(
                        "Dashboard: Error code:",
                        error.code,
                        "Message:",
                        error.message
                    );

                    // Set fallback location (Panama City coordinates as default)
                    const fallbackLocation = {
                        lat: 8.983333, // Panama City latitude
                        lng: -79.516667, // Panama City longitude
                    };

                    console.log(
                        "Dashboard: Using fallback location:",
                        fallbackLocation
                    );
                    setMylocation(fallbackLocation);
                    setLocationStatus("fallback");
                },
                {
                    enableHighAccuracy: false,
                    timeout: 10000, // 10 seconds timeout
                    maximumAge: 300000, // 5 minutes cache
                }
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (
            countrylistdata &&
            countrylistdata.countries &&
            Array.isArray(countrylistdata.countries)
        ) {
            setCountries(countrylistdata.countries);
            let country = countrylistdata.countries;
            for (let i = 0; i < country.length; i++) {
                if (country[i] && country[i].pos === 0) {
                    setSelectedCountry(country[i].country_code);
                }
            }
        } else {
            setCountries([]);
        }
    }, [countrylistdata]);

    // Analytics tracking for dashboard (driver home)
    useEffect(() => {
        // Track page view
        logPageView({
            title: "Dashboard",
            userType: auth?.profile?.usertype || "admin",
            pageType: "dashboard_page",
        });

        // If user is driver, log driver home
        if (auth?.profile?.usertype === "driver") {
            logDriverHome();
        }
    }, [logPageView, logDriverHome, auth]);

    useEffect(() => {
        if (
            countrylistdata &&
            countrylistdata.countries &&
            Array.isArray(countrylistdata.countries)
        ) {
            let country = countrylistdata.countries;
            for (let i = 0; i < country.length; i++) {
                if (country[i] && selectedCountry === country[i].country_code) {
                    setData(country[i]);
                }
            }
        }
    }, [selectedCountry, countrylistdata]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (
            usersdata.drivers &&
            bookinglistdata.bookings &&
            cars &&
            Array.isArray(cars)
        ) {
            const liveBookings = bookinglistdata.bookings.filter(
                (bkg) => bkg.status === "STARTED"
            );
            const drivers = usersdata.drivers;
            let locs = [];

            console.log(
                "Dashboard: Processing drivers data:",
                drivers.length,
                "drivers found"
            );

            for (let i = 0; i < drivers.length; i++) {
                const driver = drivers[i];

                // Check if driver has valid location data
                if (
                    driver &&
                    driver.location &&
                    driver.location.lat &&
                    driver.location.lng
                ) {
                    let carImage =
                        "https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png";
                    let bookingRef = null;

                    // Find car image based on car type
                    if (driver.carType && cars.length > 0) {
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j] && cars[j].name === driver.carType) {
                                carImage = cars[j].image || carImage;
                                break;
                            }
                        }
                    }

                    // Find active booking for this driver
                    if (liveBookings.length > 0) {
                        for (let j = 0; j < liveBookings.length; j++) {
                            if (
                                liveBookings[j] &&
                                liveBookings[j].driver === driver.id
                            ) {
                                bookingRef = liveBookings[j].reference;
                                break;
                            }
                        }
                    }

                    locs.push({
                        id: driver.id, // Use actual driver ID instead of loop index
                        lat: driver.location.lat,
                        lng: driver.location.lng,
                        drivername:
                            (driver.firstName || "") +
                            " " +
                            (driver.lastName || ""),
                        carnumber: driver.vehicleNumber || "",
                        cartype: driver.carType || "",
                        carImage: carImage,
                        bookingRef: bookingRef,
                    });
                } else {
                    console.log(
                        "Dashboard: Skipping driver without location:",
                        driver
                    );
                }
            }

            console.log(
                "Dashboard: Generated locations for map:",
                locs.length,
                "markers"
            );
            setLocations(locs);
        } else {
            console.log(
                "Dashboard: Missing required data - drivers:",
                !!usersdata.drivers,
                "bookings:",
                !!bookinglistdata.bookings,
                "cars:",
                !!cars
            );
            setLocations([]);
        }
    }, [usersdata.drivers, cars, bookinglistdata.bookings]);

    const handleCountrySelect = useCallback(
        (event) => {
            let arr = [];
            if (bookinglistdata.bookings) {
                for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                    if (
                        bookinglistdata.bookings[i].country ===
                        event.target.value
                    ) {
                        arr.push(bookinglistdata.bookings[i]);
                    }
                }
            }
            setSelectedCountry(event.target.value);
        },
        [bookinglistdata.bookings]
    );

    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if (
                    (bookinglistdata.bookings[i].status === "PAID" ||
                        bookinglistdata.bookings[i].status === "COMPLETE") &&
                    bookinglistdata.bookings[i].country === selectedCountry &&
                    ((bookinglistdata.bookings[i].fleetadmin ===
                        auth.profile.uid &&
                        auth.profile.usertype === "fleetadmin") ||
                        auth.profile.usertype === "admin")
                ) {
                    const {
                        tripdate,
                        convenience_fees,
                        fleetCommission,
                        discount,
                    } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (
                        convenience_fees &&
                        parseFloat(convenience_fees) > 0 &&
                        auth.profile.usertype === "admin"
                    ) {
                        if (
                            tDate.getDate() === today.getDate() &&
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            todayTotal =
                                todayTotal +
                                parseFloat(convenience_fees) -
                                parseFloat(discount);
                        }
                        if (
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            monthlyTotal =
                                monthlyTotal +
                                parseFloat(convenience_fees) -
                                parseFloat(discount);
                        }
                        yearlyTotal =
                            yearlyTotal +
                            parseFloat(convenience_fees) -
                            parseFloat(discount);
                    }
                    if (
                        fleetCommission &&
                        parseFloat(fleetCommission) > 0 &&
                        auth.profile.usertype === "fleetadmin"
                    ) {
                        if (
                            tDate.getDate() === today.getDate() &&
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            todayTotal =
                                todayTotal + parseFloat(fleetCommission);
                        }
                        if (
                            tDate.getMonth() === today.getMonth() &&
                            tDate.getFullYear() === today.getFullYear()
                        ) {
                            monthlyTotal =
                                monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [
        bookinglistdata.bookings,
        settings,
        auth.profile.uid,
        auth.profile.usertype,
        selectedCountry,
    ]);

    // Check PowerTranz settings
    useEffect(() => {
        const checkPowerTranzSettings = async () => {
            try {
                const response = await fetch(
                    `https://us-central1-${FirebaseConfig.projectId}.cloudfunctions.net/powertranz-config`
                );
                if (response.ok) {
                    const settings = await response.json();
                    setPowerTranzActive(settings.active || false);
                }
            } catch (error) {
                console.error("Error checking PowerTranz settings:", error);
            }
        };

        if (auth.profile.usertype === "admin") {
            checkPowerTranzSettings();
        }
    }, [auth.profile.usertype]);

    const barChartOptions = {
        labels: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ],
        colors: [colors.MAIN_COLOR, colors.MAIN_COLOR + "B2"],
        options: {
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                        },
                    },
                ],
            },
            chart: {
                height: 350,
                type: "area",
            },
            dataLabels: {
                enabled: false,
            },
            stroke: {
                curve: "smooth",
            },
        },
    };

    const barChart = [
        {
            name: t("completed_bookings"),
            data: allCompleteCount,
        },
        {
            name: t("cancelled_bookings"),
            data: allCancelCount,
        },
    ];
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === "driver") {
                    if (
                        (auth.profile.usertype === "fleetadmin" &&
                            usersdata.users[i].fleetadmin ===
                                auth.profile.uid) ||
                        auth.profile.usertype === "admin"
                    ) {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if (
                        (auth.profile.usertype === "fleetadmin" &&
                            usersdata.users[i].fleetadmin ===
                                auth.profile.uid) ||
                        auth.profile.usertype === "admin"
                    ) {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === "customer") {
                    if (
                        (auth.profile.usertype === "fleetadmin" &&
                            usersdata.users[i].fleetadmin ===
                                auth.profile.uid) ||
                        auth.profile.usertype === "admin"
                    ) {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0);
            setDriverCount(0);
            setCustomerCount(0);
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (
                        months[i] === months[tDate.getMonth()] &&
                        (bookinglistdata.bookings[j].status === "PAID" ||
                            bookinglistdata.bookings[j].status === "COMPLETE")
                    ) {
                        completeCount = completeCount + 1;
                    }
                    if (
                        months[i] === months[tDate.getMonth()] &&
                        bookinglistdata.bookings[j].status === "CANCELLED"
                    ) {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    // Check if new dashboard UI is enabled
    const isDashboardNewUIEnabled = settings?.dashboard_new_ui === true;

    // Function to refresh data (for new UI) - using GeoQuery
    const handleRefresh = useCallback(() => {
        getDriversGeoQuery();
    }, [getDriversGeoQuery]);

    // Prepare data for components
    const componentProps = useMemo(
        () => ({
            customerCount,
            driverCount,
            activeCount,
            powerTranzActive,
            auth,
            mylocation,
            locationStatus,
            locations,
            activeBookings: bookinglistdata.bookings || [], // Add booking data
            countries,
            selectedCountry,
            handleCountrySelect,
            dailygross,
            monthlygross,
            totalgross,
            data,
            barChartOptions: {
                labels: [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                ],
                colors: [colors.MAIN_COLOR, colors.MAIN_COLOR + "B2"],
                options: {
                    maintainAspectRatio: false,
                    scales: {
                        yAxes: [
                            {
                                ticks: {
                                    beginAtZero: true,
                                },
                            },
                        ],
                    },
                    chart: {
                        height: 350,
                        type: "area",
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    stroke: {
                        curve: "smooth",
                    },
                },
            },
            barChart: [
                {
                    name: t("completed_bookings"),
                    data: allCompleteCount,
                },
                {
                    name: t("cancelled_bookings"),
                    data: allCancelCount,
                },
            ],
            onRefresh: handleRefresh,
        }),
        [
            customerCount,
            driverCount,
            activeCount,
            powerTranzActive,
            auth,
            mylocation,
            locationStatus,
            locations,
            bookinglistdata.bookings, // Add booking data dependency
            countries,
            selectedCountry,
            handleCountrySelect,
            dailygross,
            monthlygross,
            totalgross,
            data,
            allCompleteCount,
            allCancelCount,
            t,
            handleRefresh,
        ]
    );

    return bookinglistdata.loading || usersdata.loading ? (
        <CircularLoading />
    ) : isDashboardNewUIEnabled ? (
        <DashboardNewUI {...componentProps} />
    ) : (
        <DashboardOldUI {...componentProps} />
    );
};

export default Dashboard;
